import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createFootprintFlightApi from 'api/flightFootprint';
import { showToast } from 'modules/app/toasterSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { AppThunk, RootState } from 'store';
import axios, { CancelTokenSource } from 'axios';
import { fixFloat } from 'utils/numberFormatter';
import createFlightApi from 'api/flightOffset';
import { currentUserSelector, customMetadataSelector } from 'modules/user/userSlice';
import checkJob from 'utils/checkJob';

const CancelToken = axios.CancelToken;
let source: CancelTokenSource = CancelToken.source();

//region types
interface CalculateFromToFootprintParams {
  from: AirportOption;
  to: AirportOption;
  via?: AirportOption;
  travelClass: TravelClass | undefined;
  travellers: number;
  flights: number;
  roundTrip: boolean;
}

interface FootprintFromTo {
  amount?: number;
  totalKilosCo2?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  flightPax: number;
}

interface ByDistanceFlightUpdateValues {
  flightDistance: number;
  travelClass: Option;
  roundTrip: boolean;
  travellers: number;
  kilosCo2: number;
  flightPax: number;
}
interface FootprintInfo {
  emissionName?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  [key: string]: string | Option | Date | undefined | boolean | number;
}

export interface CustomMetadata {
  [key: string]: string | Option | Date | number | undefined | boolean | string[] | ByDistanceFlight[] | PartnersCustomerOption | File;
}

export interface FlightFootprintState extends CustomMetadata {
  mode?: FlightOffsetMode;
  amount?: number;
  totalKilosCo2?: number;
  from?: AirportOption;
  to?: AirportOption;
  via?: AirportOption;
  travellers?: number;
  travelClass?: { label: string; value: TravelClass };
  flights?: number;
  roundTrip?: boolean;
  flightPax?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  isLoading: boolean;
  emissionName?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  timeFrameMonth?: Option;
  timeFrameMode: TimeFrameMode;
  isFootprintInfoInvalid: boolean;
  isTimeFrameInfoInvalid: boolean;
  selectedCustomMetadata?: string[];
  byDistanceFlights: ByDistanceFlight[];
  fileUploading?: boolean;
  storageBlobName?: string;
  fileErrors?: string[];
  fileSuccess?: boolean;
  customer?: PartnersCustomerOption;
  claimCode?: string;
  inviteId?: string;
  isMultiUpload?: boolean;
  footprintsCount?: number;
  fullOffsetRows?: undefined;
  isUploadingFileCancelled?: boolean;
  fileProcessing?: boolean;
  progressPercentage?: number;
}

//endregion

const initialState: FlightFootprintState = {
  isLoading: false,
  roundTrip: false,
  byDistanceFlights: [],
  isFootprintInfoInvalid: true,
  isTimeFrameInfoInvalid: true,
  timeFrameMode: 'month',
};

const tonnesFactor = 1000;

const slice = createSlice({
  name: 'flightFootprint',
  initialState,
  reducers: {
    reset: (state: FlightFootprintState): FlightFootprintState => {
      return { mode: state.mode, ...initialState };
    },
    resetAmount: (state: FlightFootprintState): FlightFootprintState => {
      return { ...state, amount: 0, totalKilosCo2: 0 };
    },
    resetCustomer: (state: FlightFootprintState): FlightFootprintState => {
      return { ...state, customer: undefined };
    },
    setLoading: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      state.isLoading = action.payload;
      return state;
    },
    setFootprintInfo: (state: FlightFootprintState, action: PayloadAction<FootprintInfo>): FlightFootprintState => {
      Object.keys(action.payload).map(key => {
        state[key] = action.payload[key];
      });
      return state;
    },
    setFootprintInfoInvalid: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      state.isFootprintInfoInvalid = action.payload;
      return state;
    },
    setTimeFrameInfoInvalid: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      state.isTimeFrameInfoInvalid = action.payload;
      return state;
    },
    setMode: (state: FlightFootprintState, action: PayloadAction<FlightOffsetMode>): FlightFootprintState => {
      state.mode = action.payload;
      return state;
    },
    setSelectedCustomMetadata: (state: FlightFootprintState, action: PayloadAction<string[]>): FlightFootprintState => {
      state.selectedCustomMetadata = action.payload;
      return state;
    },
    setOffsetFootprint: (state: FlightFootprintState, action: PayloadAction<FootprintFromTo>): FlightFootprintState => {
      return { ...state, ...action.payload };
    },
    addByDistanceFlightRow: (state: FlightFootprintState, action: PayloadAction<ByDistanceFlight>): FlightFootprintState => {
      state.byDistanceFlights.push(action.payload);
      return state;
    },
    removeByDistanceFlightRow: (state: FlightFootprintState, action: PayloadAction<number>): FlightFootprintState => {
      const filteredFligths = state.byDistanceFlights.filter((_, i) => i !== action.payload);
      state.amount = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2 / tonnesFactor, 0);
      state.totalKilosCo2 = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2, 0);
      state.byDistanceFlights = filteredFligths;
      return state;
    },
    setByDistanceFlightLength: (
      state: FlightFootprintState,
      action: PayloadAction<{ index: number; value: FlightLengthOption }>,
    ): FlightFootprintState => {
      state.byDistanceFlights[action.payload.index].flightLength = action.payload.value;
      return state;
    },
    setByDistanceFlight: (
      state: FlightFootprintState,
      action: PayloadAction<{ index: number; value: ByDistanceFlightUpdateValues }>,
    ): FlightFootprintState => {
      state.byDistanceFlights[action.payload.index] = { ...state.byDistanceFlights[action.payload.index], ...action.payload.value };
      return state;
    },
    setByDistanceTotalAmount: (
      state: FlightFootprintState,
      action: PayloadAction<{ amount: number; kilosCo2: number; index?: number }>,
    ): FlightFootprintState => {
      if (typeof action.payload.index === 'number') {
        const filteredFligths = state.byDistanceFlights.filter((_, i) => i !== action.payload.index);
        state.amount = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2 / tonnesFactor, action.payload.amount);
        state.totalKilosCo2 = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2, action.payload.kilosCo2);
      } else {
        const previousAmount = state.amount ?? 0;
        const previousKilosCo2 = state.totalKilosCo2 ?? 0;
        state.amount = previousAmount + action.payload.amount;
        state.totalKilosCo2 = previousKilosCo2 + action.payload.kilosCo2;
      }
      return state;
    },
    resetUploadState: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      if (!action.payload) {
        source.cancel();
      }
      state.fileUploading = action.payload;
      state.fileProcessing = action.payload;
      state.progressPercentage = 0;
      state.isUploadingFileCancelled = true;
      state.fileErrors = [];
      state.fileSuccess = false;
      state.amount = undefined;
      state.totalKilosCo2 = undefined;
      state.footprintsCount = undefined;
      return state;
    },
    resetUploadingFileCancelled: (state: FlightFootprintState): FlightFootprintState => {
      state.isUploadingFileCancelled = false;
      return state;
    },
    setFileUploading: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      state.fileUploading = action.payload;
      return state;
    },
    setFootprintsCount: (state: FlightFootprintState, action: PayloadAction<number>): FlightFootprintState => {
      state.footprintsCount = action.payload;
      return state;
    },
    setFileErrors: (state: FlightFootprintState, action: PayloadAction<string[]>): FlightFootprintState => {
      state.fileErrors = action.payload;
      return state;
    },
    setFileSuccess: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      state.fileSuccess = action.payload;
      return state;
    },
    setUploadTotalAmount: (state: FlightFootprintState, action: PayloadAction<{ amount: number; kilosCo2: number }>): FlightFootprintState => {
      state.amount = action.payload.amount;
      state.totalKilosCo2 = action.payload.kilosCo2;
      return state;
    },
    setProgressPercentage: (state: FlightFootprintState, action: PayloadAction<number>): FlightFootprintState => {
      state.progressPercentage = action.payload;
      return state;
    },
    setFileProcessing: (state: FlightFootprintState, action: PayloadAction<boolean>): FlightFootprintState => {
      state.fileProcessing = action.payload;
      return state;
    },
    setStorageBlobName: (state: FlightFootprintState, action: PayloadAction<string>): FlightFootprintState => {
      state.storageBlobName = action.payload;
      return state;
    },
  },
});

const {
  reset,
  setFootprintInfo,
  setLoading,
  resetAmount,
  setFootprintInfoInvalid,
  setTimeFrameInfoInvalid,
  setSelectedCustomMetadata,
  setMode,
  setOffsetFootprint,
  addByDistanceFlightRow,
  removeByDistanceFlightRow,
  setByDistanceFlightLength,
  setByDistanceTotalAmount,
  setByDistanceFlight,
  resetUploadState,
  setFileUploading,
  setFileErrors,
  setFileSuccess,
  setUploadTotalAmount,
  setFootprintsCount,
  resetCustomer,
  resetUploadingFileCancelled,
  setProgressPercentage,
  setFileProcessing,
  setStorageBlobName,
} = slice.actions;

export {
  reset,
  setFootprintInfo,
  resetAmount,
  setFootprintInfoInvalid,
  setTimeFrameInfoInvalid,
  setSelectedCustomMetadata,
  setMode,
  removeByDistanceFlightRow,
  setByDistanceFlightLength,
  setByDistanceTotalAmount,
  resetUploadState,
  resetCustomer,
};
export default slice.reducer;

export const calculateFromToOffset = (params: CalculateFromToFootprintParams): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { from, to, via, travelClass, travellers, flights, roundTrip } = params;

  if (!from || !to || !travelClass || !travellers || !flights) {
    return;
  }

  dispatch(setLoading(true));

  let offset;
  let partialOffsetFromVia;
  let partialOffsetViaTo;

  try {
    if (via) {
      partialOffsetFromVia = await flightOffsetApi.getFlightOffset({ ...params, from: from.value, to: via.value });
      partialOffsetViaTo = await flightOffsetApi.getFlightOffset({ ...params, from: via.value, to: to.value });

      offset = {
        kilosCo2e:
          partialOffsetFromVia.kilosCo2e && partialOffsetViaTo.kilosCo2e
            ? partialOffsetFromVia?.kilosCo2e + partialOffsetViaTo?.kilosCo2e
            : undefined,
        kilosCo2: partialOffsetFromVia.kilosCo2 + partialOffsetViaTo.kilosCo2,
        distanceKm: partialOffsetFromVia.distanceKm + partialOffsetViaTo.distanceKm,
      };
    } else {
      offset = await flightOffsetApi.getFlightOffset({ travellers, flights, roundTrip, travelClass, from: from.value, to: to.value });
    }

    let multiplicator = roundTrip ? 2 : 1;
    const kilosCo2 = offset.kilosCo2e ?? offset.kilosCo2;

    if (kilosCo2 < 0) {
      dispatch(resetAmount());
      dispatch(setLoading(true));
      return;
    }

    multiplicator = via ? multiplicator * 2 : multiplicator;
    const flightPax = multiplicator * Number(params.flights || 1) * Number(params.travellers ?? 1);

    dispatch(
      setOffsetFootprint({
        amount: kilosCo2 / tonnesFactor,
        totalKilosCo2: kilosCo2,
        distance: offset.distanceKm,
        distanceFromVia: partialOffsetFromVia?.distanceKm,
        distanceViaTo: partialOffsetViaTo?.distanceKm,
        flightPax,
      }),
    );
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:fetchAmountError' }));
    dispatch(setLoading(false));
  }
};

export const createFlightFootprint = (errorCb?: CallableFunction, successCb?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const context = appContextSelector(getState());
  const flightAPI = createFootprintFlightApi(context);
  const app = getState().app;
  const currentUser = currentUserSelector(getState());
  const customMetadata = customMetadataSelector(getState()) ?? [];
  const { mode, storageBlobName, emissionName, description, ...restProps } = getState().flightFootprint;
  const isUploadMode = mode === 'upload';
  const isConnectContext = context === 'connect';
  const partnershipId = isConnectContext ? currentUser?.partnershipId ?? '' : app.partnershipId ?? '';
  const customerId = currentUser?.customerId ?? '';

  const customMetadataPayload: CustomMetadata = {};
  customMetadata?.map(
    ({ internalName }) =>
      //@ts-ignore
      (customMetadataPayload[internalName] = restProps[internalName]?.value ? restProps[internalName]?.value : restProps[internalName]),
  );

  dispatch(setLoading(true));
  if (isUploadMode) {
    try {
      source = CancelToken.source();
      if (storageBlobName && emissionName && description) {
        const payload = {
          storageBlobName,
          emissionName,
          description,
          ...customMetadataPayload,
        };
        const response = isConnectContext
          ? await flightAPI.createBatchOrderForPartner(payload, source.token, partnershipId)
          : await flightAPI.createBatchOrderForCustomer(payload, source.token, customerId);

        if (response.jobId) {
          const result = await checkJob<FlightsProcessingFileSummary>({
            partnershipId: response.partnershipId,
            jobId: response.jobId,
            context,
            module: 'flightOffset',
            setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
            setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
            setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
          });
          const errorMessages = result?.messages?.filter(({ type }) => type.toLowerCase() !== 'information')?.map(m => m.message);
          if (result.status === 'Succeeded' && errorMessages.length === 0) {
            successCb && successCb();
          } else {
            dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'footprints:createFootprintError' }));
            if (Array.isArray(errorMessages)) {
              dispatch(setFileErrors(errorMessages));
            }
            errorCb && errorCb();
          }
        }
      }

      dispatch(setLoading(false));
    } catch (err) {
      dispatch(setLoading(false));
      dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'footprints:createFootprintError' }));
      errorCb && errorCb();
    }
  }
};

export const addNewByDistanceFlightItem = (onSuccess?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);

  const item: ByDistanceFlight = {
    flightLength: {
      id: '1',
      value: 'short',
      label: 'Short (< 2h)',
      distance: 1500,
    },
    flightDistance: 1500,
    travelClass: { value: 'economy', label: 'Economy' },
    roundTrip: false,
    travellers: 1,
    kilosCo2: 0,
    flightPax: 1,
  };

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: 'economy' as TravelClass,
      roundTrip: item.roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    item.kilosCo2 = kilosCo2;

    dispatch(setByDistanceTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2 }));
    dispatch(addByDistanceFlightRow(item));
    onSuccess && onSuccess(item);
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateByDistanceFlight = (props: { index: number; value: number | Option | boolean; name: string }): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const { index, value, name } = props;
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { byDistanceFlights } = getState().flightFootprint;
  const item = byDistanceFlights[index];

  const travellers = name === 'travellers' ? (value as number) : item.travellers;
  const roundTrip = name === 'roundTrip' ? (value as boolean) : item.roundTrip;
  const flightDistance = name === 'flightDistance' ? (value as number) : item.flightDistance;
  const travelClass = name === 'travelClass' ? (value as Option) : item.travelClass;

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers,
      distance: flightDistance,
      travelClass: travelClass.value as TravelClass,
      roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(travellers ?? 1) * (roundTrip ? 2 : 1);

    dispatch(setByDistanceTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2, index }));
    dispatch(
      setByDistanceFlight({
        index,
        value: { travellers, flightDistance, travelClass, roundTrip, kilosCo2, flightPax },
      }),
    );
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const processFile = (file: File): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setFileSuccess(false));
  dispatch(setFileProcessing(true));
  dispatch(setUploadTotalAmount({ amount: 0, kilosCo2: 0 }));
  dispatch(resetUploadingFileCancelled());
  const app = getState().app;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const isConnectContext = context === 'connect';
  const flightAPI = createFootprintFlightApi(context);
  const partnershipId = isConnectContext ? currentUser?.partnershipId ?? '' : app.partnershipId ?? '';
  const customerId = currentUser?.customerId ?? '';

  try {
    source = CancelToken.source();

    const batchResponse = isConnectContext
      ? await flightAPI.uploadFootprintBatchForPartner(file, source.token, partnershipId)
      : await flightAPI.uploadFootprintBatchForCustomer(file, source.token, customerId);

    if (batchResponse.partnershipId && batchResponse.jobId) {
      const response = await checkJob<FlightsFootprintProcessingFileSummary>({
        partnershipId: batchResponse.partnershipId,
        jobId: batchResponse.jobId,
        context,
        module: 'flightOffset',
        setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
        setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
        setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
      });

      const summary = response?.summary?.footprints;
      const footprintsCount = summary?.count;
      const storageBlobName = summary?.storageBlobName;
      if (storageBlobName) {
        footprintsCount && dispatch(setFootprintsCount(footprintsCount));
        dispatch(setStorageBlobName(storageBlobName));
        dispatch(setFileErrors([]));
        dispatch(setFileSuccess(true));
        const kilosCo2 = response.summary.footprints?.totalCo2Kilos;
        dispatch(setUploadTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2 }));
      } else {
        if (Array.isArray(response.messages)) {
          const textMessages = response.messages.map(({ message }) => message);
          dispatch(setFileErrors(textMessages));
        } else setFileErrors([]);
      }
    }
  } catch (err) {
    // @ts-ignore
    if (err && err.data) {
      // @ts-ignore
      const { data } = err.data;
      if (data && Array.isArray(data)) {
        dispatch(setFileErrors(data));
        // @ts-ignore
      }
    }
    dispatch(setFileUploading(false));
  }
};

export const flightAmountSelector = createSelector(
  (state: RootState) => state.flightFootprint,
  state => {
    const { amount } = state;

    return fixFloat(amount ?? 0);
  },
);
